'use client'
import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from '@/registry/default/ui/carousel'
import { Card, CardContent } from '@/registry/default/ui/card'
import Link from 'next/link'
import React, { useEffect } from 'react'
import Autoplay from 'embla-carousel-autoplay'
import {
  AudioWaveform,
  Handshake,
  HeartHandshake,
  MailCheck,
  UserMinus,
} from 'lucide-react'
import Image from 'next/image'
import { cn } from '@/lib/utils'
import { useRouter } from 'next/navigation'
import { trpcClient } from '@/lib/trpc-client'
import { useUser } from '@/features/account/hooks/useUser'

interface HomeComponentProps {
  pics: any[]
}

export function HomeComponent({ pics }: HomeComponentProps) {
  const { user } = useUser()
  const plugin = React.useRef(
    Autoplay({ delay: 5000, stopOnInteraction: true })
  )
  const router = useRouter()
  const { data: userHome, refetch } = trpcClient.push.getUserHome.useQuery(
    {
      workspaceId: process.env.FRONT_WORKSPACE_ID ?? 'yunjiaoyi',
    },
    { enabled: !!user }
  )

  useEffect(() => {
    if(user) {
      refetch().then()
    }
  }, [user]);

  const messagePush = {
    id: 1,
    title: '我的消息',
    description:
        userHome && userHome.count > 0
        ? userHome?.last?.workspacePush.content
        : '没有新消息',
    badge: userHome?.count ?? 0,
    icon: MailCheck,
    show: true,
    url: '/notification',
  }

  const menuItems = [
    {
      id: 2,
      title: '创建昵称',
      description: '为自己取个响亮的昵称才能参与跟投哦~',
      icon: UserMinus,
      badge: 0,
      show: user?.name?.length == 0,
      url: '/nickname',
    },
    {
      id: 3,
      title: '实盘合作T+D递延提醒',
      description: `当前有 ${userHome?.realTDCount??0} 个T+D合作策略可递延`,
      highlight: '0',
      icon: Handshake,
      badge: 0,
      show: true,
      url: '/td-extension',
    },
    {
      id: 4,
      title: '实盘合作',
      description: `当前有 ${userHome?.realCount??0} 个进行中合作策略`,
      highlight: '0',
      badge: 0,
      show: true,
      icon: HeartHandshake,
      url: '/portfolio',
    },
    {
      id: 5,
      title: '模拟参赛',
      description: `当前有 ${userHome?.virtualCount??0} 个进行中合作策略`,
      highlight: '0',
      badge: 0,
      show: true,
      icon: AudioWaveform,
      url: '/portfolio-simulation',
    },
    {
      id: 6,
      title: '模拟参赛T+D递延提醒',
      description: `当前有 ${userHome?.virtualTDCount??0} 个模拟参赛T+D策略可递延`,
      highlight: '0',
      badge: 0,
      show: true,
      icon: Handshake,
      url: '/td-extension-simulation',
    },
  ]

  if(userHome && userHome.count > 0) {
    menuItems.unshift(messagePush)
  } else {
    menuItems.push(messagePush)
  }
  return (
    <div className="py-0 gap-0 w-full md:max-w-xl divide-y border-b  px-0  flex flex-col ">
      <Carousel
        plugins={[plugin.current]}
        className="w-full md:max-w-xl"
        onMouseEnter={plugin.current.stop}
        onMouseLeave={plugin.current.reset}
      >
        <CarouselContent>
          {pics.map((item, index) => (
            <CarouselItem
              key={index}
              onClick={() => {
                router.push(item.link)
              }}
            >
              <div className="px-0">
                <Card className={'border-0 rounded-none shadow-none'}>
                  <CardContent className="flex aspect-video p-0">
                    <Image
                      src={item.sourceUrl}
                      alt={item.title}
                      width={1080}
                      priority={true}
                      height={720}
                      className={cn(' w-full h-full  object-cover z-0')}
                    />
                    <span className="absolute bottom-0 flex w-full bg-foreground/50 h-10 text-background items-center justify-center text-md font-semibold">
                      {item.title}
                    </span>
                  </CardContent>
                </Card>
              </div>
            </CarouselItem>
          ))}
        </CarouselContent>
      </Carousel>

      {menuItems.map((item) => {
        return (
          item.show && (
            <Link key={item.id} href={user ? item.url : '/login'}>
              <Card className="flex items-center border-0 rounded-none shadow-none p-4 space-x-4">
                <div className="shrink-0">
                  <item.icon className={'w-7 h-7'} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h2 className="font-medium">{item.title}</h2>
                    {item?.badge > 0 && (
                      <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-red-500 text-white text-xs">
                        {item.badge}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">
                    {item.description.replace(
                      item.highlight ? `${item.highlight}` : '',
                      item.highlight ? `${item.highlight}` : ''
                    )}
                  </p>
                </div>
              </Card>
            </Link>
          )
        )
      })}
    </div>
  )
}
